"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.abortableFetch = void 0;
let lifecycleListenersRegistered = false;
let pageIsUnloading = false;
const activeControllers = new Set();
/**
 * Page teardown fetch cleanup.
 *
 * Closing a tab while a native fetch is still pending can leave Chromium's
 * renderer doing expensive V8/browser lifecycle work. In this application it
 * manifested as other same-origin tabs freezing as well, because they can share
 * the same renderer process. The safest thing we can do from the app is to stop
 * pending fetches once the browser has actually accepted the navigation/tab
 * close, and prevent their promise continuations from running during teardown.
 *
 * We deliberately do not do this in `beforeunload`: that event can still be
 * cancelled by the user, so aborting there would break saves even if the user
 * decides to stay on the page. `pagehide` runs later, when the page is really
 * being hidden/discarded. If `event.persisted` is true, the page is only entering
 * the back-forward cache and may be restored, so requests are left untouched.
 */
function registerPageLifecycleCleanup() {
    if (lifecycleListenersRegistered || typeof window === 'undefined') {
        return;
    }
    /**
     * Abort all in-flight requests when the page is actually being discarded.
     *
     * We intentionally use `pagehide` instead of `beforeunload`:
     * - `beforeunload` may be cancelled by the user, so aborting there would break
     *   requests even when the user decides to stay on the page.
     * - `pagehide` runs after the browser has accepted the navigation/tab close.
     * - `event.persisted` is true when the page enters bfcache; in that case the
     *   page may be restored later, so we do not abort requests just because it was
     *   cached.
     */
    window.addEventListener('pagehide', (event) => {
        if (event.persisted) {
            return;
        }
        pageIsUnloading = true;
        activeControllers.forEach((controller) => controller.abort());
        activeControllers.clear();
    });
    lifecycleListenersRegistered = true;
}
function abortableFetch(request, opts) {
    const controller = new AbortController();
    registerPageLifecycleCleanup();
    activeControllers.add(controller);
    const response = fetch(request, {
        ...opts,
        signal: controller.signal,
    })
        .finally(() => {
        activeControllers.delete(controller);
    })
        .catch((error) => {
        if (pageIsUnloading) {
            return waitForever();
        }
        throw error;
    });
    return {
        abort: () => controller.abort(),
        response,
        raw: async () => {
            const res = await response;
            if (pageIsUnloading) {
                return waitForever();
            }
            if (!res.ok) {
                const data = await res.json();
                throw data;
            }
            return res;
        },
        json: async () => {
            const res = await response;
            if (pageIsUnloading) {
                return waitForever();
            }
            const data = await res.json();
            if (pageIsUnloading) {
                return waitForever();
            }
            if (!res.ok) {
                throw data;
            }
            return data;
        },
        text: async () => {
            const res = await response;
            if (pageIsUnloading) {
                return waitForever();
            }
            const data = await res.text();
            if (pageIsUnloading) {
                return waitForever();
            }
            if (!res.ok) {
                throw data;
            }
            return data;
        },
        none: async () => {
            var _a;
            const res = await response;
            if (pageIsUnloading) {
                return waitForever();
            }
            if (res.status === 403) {
                const json = await res.json();
                throw { message: (_a = json === null || json === void 0 ? void 0 : json.message) !== null && _a !== void 0 ? _a : 'Přístup odepřen' };
            }
            else if (!res.ok) {
                throw await res.json();
            }
            return;
        },
    };
}
exports.abortableFetch = abortableFetch;
function waitForever() {
    /**
     * During page teardown aborted fetches should not continue through normal
     * success/error handlers. Those handlers commonly trigger React state updates,
     * snackbars, refreshes, and other cleanup while the renderer is being
     * destroyed. Returning a never-settling promise keeps the original consumer
     * suspended until the page is gone.
     */
    return new Promise(() => undefined);
}
//# sourceMappingURL=abortable-fetch.js.map